import { useNavigate } from "react-router-dom";

/**
 * The onboarding checklist, shown only while setup is incomplete.
 *
 * Rendered from the real `onboarding.steps` payload rather than a fixed list —
 * the prototype mocked five steps, the service currently defines three
 * (`integrations`, `flow`, `channel`), and hardcoding either number would make
 * the UI lie the moment the service changes.
 *
 * Every row is navigable. The steps are *not* sequentially dependent —
 * `computeAutoStatuses` evaluates each one independently against real data — so
 * refusing to open a later step would be the UI inventing a constraint the
 * platform doesn't have. What the emphasis conveys is priority, not permission:
 * one step is the suggested next move, the rest stay quiet.
 *
 * The states are given distinct marks rather than distinct opacities. A
 * struck-through label reads as "cancelled", and a dimmed label reads as
 * "disabled" — both were misread exactly that way. Completion is a check, a
 * pending step is an arrow, and only the current one gets a filled button.
 *
 * The check glyph is reserved for completion the platform actually verified.
 * `effectiveStatus` collapses two very different facts into one word: a step
 * can be "completed" because the data is there, or because an admin ticked it
 * by hand (`overrideStatus`). Showing both as a green check makes the screen
 * assert something nobody checked — which is exactly how the home came to say
 * "WhatsApp concluído" on an account with no WhatsApp.
 */

function CheckGlyph() {
  return (
    <svg
      viewBox="0 0 16 16"
      aria-hidden="true"
      className="h-3.5 w-3.5 flex-none text-[#12a06c] dark:text-[#7ee2a8]"
    >
      <path
        d="M3 8.4l3.2 3.2L13 4.8"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

/**
 * Where the decorative bar is drawn, which is not where the count is.
 *
 * Endowed progress (Nunes & Drèze, 2006): a 12-slot loyalty card handed over
 * with 2 slots already stamped gets completed more often than a 10-slot card
 * with none — an effort already begun pulls harder than one not yet started. So
 * an untouched checklist starts the bar slightly filled instead of at zero.
 *
 * The nudge is confined to the decoration. `<progress>` and the "0 de 3"
 * counter beside it keep reporting the real number, and the floor is capped at
 * half of what a single step is worth — the moment the painted bar could be
 * mistaken for a finished step, it stops being a nudge and becomes a claim.
 */
export function displayedProgress(progress, stepCount) {
  if (progress >= 100 || !stepCount) return progress;
  const halfOfOneStep = 100 / stepCount / 2;
  return Math.max(progress, Math.min(10, halfOfOneStep));
}

/**
 * Whether this step is only "done" because someone said so.
 *
 * Requires `autoStatus` to be present and disagree — an older payload without
 * it carries no evidence either way, and inventing a warning from missing data
 * would be its own kind of lie.
 */
function isManuallyMarked(step) {
  return (
    step.overrideStatus === "completed" &&
    !!step.autoStatus &&
    step.autoStatus !== "completed"
  );
}

export default function OnboardingChecklist({ onboarding, stepConfig }) {
  const navigate = useNavigate();
  const steps = onboarding?.steps || [];
  if (!steps.length) return null;

  const completed = steps.filter((s) => s.effectiveStatus === "completed").length;
  const remaining = steps.length - completed;
  const currentIndex = steps.findIndex((s) => s.effectiveStatus !== "completed");
  const progress = Math.round((completed / steps.length) * 100);
  const painted = displayedProgress(progress, steps.length);

  return (
    <div className="mb-[18px] rounded-[22px] border border-black/[.06] bg-[linear-gradient(150deg,rgba(64,222,168,.14),rgba(96,158,255,.09)_55%,rgba(255,255,255,.5))] px-8 pb-[22px] pt-7 dark:border-transparent dark:bg-[linear-gradient(150deg,rgba(110,200,160,.13),rgba(80,140,255,.07)_55%,rgba(255,255,255,.028))]">
      <div className="flex flex-wrap items-baseline gap-4">
        <div className="text-xl font-bold leading-[1.2] tracking-[-.024em] text-[#15161f] dark:text-white">
          {remaining === 1
            ? "Falta 1 passo para você começar a atender"
            : `Faltam ${remaining} passos para você começar a atender`}
        </div>
        <div className="ml-auto whitespace-nowrap text-[12.5px] leading-none text-[#5c5f70] dark:text-white/50">
          {/* Piso de 1, pelo mesmo motivo da barra: um contador zerado convida a
              abandonar. O `<progress>` continua reportando o número real. */}
          {Math.max(completed, 1)} de {steps.length}
        </div>
      </div>

      {/* The native element carries the semantics; the div below is the visual
          treatment, since a gradient fill can't be styled on <progress>
          consistently across browsers. */}
      <progress className="sr-only" value={progress} max={100}>
        {progress}% da configuração concluída
      </progress>
      <div
        aria-hidden="true"
        className="mb-1 mt-[18px] h-0.5 overflow-hidden rounded-sm bg-black/10 dark:bg-white/[.12]"
      >
        <div
          data-progress-fill={painted}
          className="h-full bg-[linear-gradient(90deg,#12a06c,#4f7dff)] transition-[width] duration-500 dark:bg-[linear-gradient(90deg,#7ee2a8,#7b9bff)]"
          style={{ width: `${painted}%` }}
        />
      </div>

      <div className="flex flex-col">
        {steps.map((step, index) => {
          const isDone = step.effectiveStatus === "completed";
          const isManual = isDone && isManuallyMarked(step);
          const isVerified = isDone && !isManual;
          const isCurrent = index === currentIndex;
          const config = stepConfig?.[step.key];
          const label = config?.label || step.label;
          const ordinal = String(index + 1).padStart(2, "0");

          let state = "pendente";
          if (isManual) state = "marcado manualmente";
          else if (isDone) state = "concluído";
          else if (isCurrent) state = "próximo passo";

          return (
            <button
              key={step.key}
              type="button"
              aria-label={`${label} — ${state}`}
              onClick={() => config?.path && navigate(config.path)}
              className={`group -mx-2 flex items-center gap-3.5 rounded-xl px-2 text-left transition-colors hover:bg-black/[.035] dark:hover:bg-white/[.05] ${
                isCurrent ? "py-[13px]" : "py-[11px]"
              }`}
            >
              {isVerified ? (
                <CheckGlyph />
              ) : (
                <span
                  className={`w-3.5 flex-none font-mono text-[11.5px] leading-none ${
                    isCurrent
                      ? "font-medium text-[#15161f] dark:text-white"
                      : "text-[#8f92a2] dark:text-white/40"
                  }`}
                >
                  {ordinal}
                </span>
              )}

              <span
                className={
                  isCurrent
                    ? "text-[15px] font-bold leading-none text-[#15161f] dark:text-white"
                    : `text-[13.5px] font-medium leading-none ${
                        isDone
                          ? "text-[#6f7284] dark:text-white/[.55]"
                          : "text-[#767989] dark:text-white/[.5]"
                      }`
                }
              >
                {label}
              </span>

              {isCurrent && (
                <span className="ml-auto inline-flex h-8 flex-none items-center whitespace-nowrap rounded-2xl bg-[#15161f] px-4 text-xs font-bold text-white transition-opacity group-hover:opacity-90 dark:bg-white dark:text-[#0a0a0f]">
                  Continuar
                </span>
              )}

              {isVerified && (
                <span className="ml-auto whitespace-nowrap font-mono text-[10px] uppercase tracking-[.12em] text-[#12a06c] dark:text-[#7ee2a8]">
                  Concluído
                </span>
              )}

              {isManual && (
                <span
                  title="Marcado por um administrador — a plataforma não encontrou esse item na conta."
                  className="ml-auto whitespace-nowrap font-mono text-[10px] uppercase tracking-[.12em] text-[#b06a00] dark:text-[#f0b45a]"
                >
                  Marcado manualmente
                </span>
              )}

              {!isCurrent && !isDone && (
                <span
                  aria-hidden="true"
                  className="ml-auto flex-none text-[13px] leading-none text-[#8f92a2] opacity-0 transition-opacity group-hover:opacity-100 dark:text-white/50"
                >
                  →
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}
